import { Injectable } from '@angular/core';
import { Observable, map, of, BehaviorSubject, debounceTime, switchMap } from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { Address } from '../../interfaces/location/address.interface';
import { LocationService } from '../location/location.service';
import { City } from '../../interfaces/location/city.interface';
import { Route } from '../../interfaces/route-planning/gtfs/route.interface';
import { ScheduleService } from '../schedule/schedule.service';
import { Stop } from '../../interfaces/route-planning/gtfs/stop.interface';
import { AutoCompleteRepository } from '../../repositories/auto-complete.repository';
import { PointOfInterest } from '../../interfaces/location/point-of-interest.interface';
import { Coordinate } from '../../interfaces/location/coordinate.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';

@Injectable({
  providedIn: 'root',
})
export class AutoCompleteService {
  public static readonly AUTO_COMPLETE_MIN_CHAR: number = 2;
  public static readonly LINE_AUTO_COMPLETE_MIN_CHAR: number = 1;
  public static readonly STATION_AUTO_COMPLETE_MIN_CHAR: number = 1;
  public static readonly AUTO_COMPLETE_DEBOUNCE_TIME: number = 300;

  private lineSubject = new BehaviorSubject<string>('');
  private locationSubject = new BehaviorSubject<string>('');
  private poiSubject = new BehaviorSubject<string>('');
  private stationSubject = new BehaviorSubject<string>('');

  constructor(
    private readonly locationsService: LocationService,
    private readonly scheduleService: ScheduleService,
    private readonly autoCompleteRepository: AutoCompleteRepository,
    private readonly languageService: LanguageService
  ) {}

  searchLines(lineTerm: string): Observable<SelectOption<Route>[]> {
    this.lineSubject.next(lineTerm);

    return this.lineSubject.pipe(
      debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
      switchMap(term => {
        if (
          !!term &&
          term.length >= AutoCompleteService.LINE_AUTO_COMPLETE_MIN_CHAR
        ) {
          LogService.logDebug('autocomplete line ', term);

          return this.autoCompleteRepository.searchLines(term).pipe(
            map<ServiceResponse<Route[]>, SelectOption<Route>[]>((routes) =>
              routes.data.map((route) => ({
                label: route.routeName,
                value: route,
              }))
            )
          );
        }
        return of([]);
      })
    );
  }

  searchLocations(searchTerm: string): Observable<SelectOption<Address>[]> {
  
    if (
      !!searchTerm &&
      searchTerm.length >= AutoCompleteService.AUTO_COMPLETE_MIN_CHAR
    ) {
      LogService.logDebug('autocomplete locations', searchTerm);
  
      return this.autoCompleteRepository.searchLocations(searchTerm).pipe(
        map((addresses) =>
          addresses.data.map((address) => ({
            label: address.addressDetails.label,
            value: address,
          }))
        )
      );
    }
  
    return of([]);
  }
  

  /**
   * Stateless per call — no shared Subject: origin + destination fields subscribe in parallel;
   * a shared stream caused lost keystrokes (e.g. spaces). Debounce on valueChanges at call site.
   */
  searchCities(cityTerm: string): Observable<SelectOption<City>[]> {
    if (typeof cityTerm !== 'string') {
      return of([]);
    }
    if (
      !cityTerm ||
      cityTerm.length < AutoCompleteService.AUTO_COMPLETE_MIN_CHAR
    ) {
      return of([]);
    }
    LogService.logDebug('autocomplete city ', cityTerm);
    return this.autoCompleteRepository.searchCities(cityTerm).pipe(
      map<ServiceResponse<City[]>, SelectOption<City>[]>((cities) =>
        cities.data.map((city) => ({
          label: city.cityName,
          value: city,
        }))
      )
    );
  }

  searchPointsOfInterest(poiTerm: string): Observable<SelectOption<PointOfInterest>[]> {
    this.poiSubject.next(poiTerm);

    return this.poiSubject.pipe(
      debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
      switchMap(term => {
        if (
          !!term &&
          term.length >= AutoCompleteService.AUTO_COMPLETE_MIN_CHAR
        ) {
          LogService.logDebug('autocomplete city ', term);

          return this.autoCompleteRepository.searchPointsOfInterest(term).pipe(
            map<ServiceResponse<PointOfInterest[]>, SelectOption<PointOfInterest>[]>((pois) =>
              pois.data.map((poi) => ({
                label: poi.name,
                value: poi,
              }))
            )
          );
        }
        return of([]);
      })
    );
  }

  searchStations(stationTerm: string): Observable<SelectOption<Stop>[]> {
    this.stationSubject.next(stationTerm);
    const language = this.languageService.getSelectedLanguage();

    return this.stationSubject.pipe(
      debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
      switchMap(term => {
        if (
          !!term &&
          term.length >= AutoCompleteService.STATION_AUTO_COMPLETE_MIN_CHAR
        ) {
          LogService.logDebug('autocomplete station ', term);

          return this.autoCompleteRepository.searchStation(term).pipe(
            map<ServiceResponse<Stop[]>, SelectOption<Stop>[]>((stations) =>
              stations.data.map((station) => ({
                label: station.name[language.symbol] || station.name.he || '',
                value: station,
              }))
            )
          );
        }
        return of([]);
      })
    );
  }

  addMissingSearch(searchText: string): Observable<ServiceResponse<void>> {
    return this.autoCompleteRepository.addMissingSearch(searchText);
  }

  getAddressDataByCoordinates(
    coordinates: Coordinate
  ): Observable<ServiceResponse<Address>> {
    return this.autoCompleteRepository.getAddressDataByCoordinates(coordinates);
  }
}
